"use client";
import { ScheduledDatePicker } from "@/components/scheduledDatePicker/scheduledDatePicker";
import { OrderType, ServiceType } from "@/constants/enum";
import { DayPart } from "@/dto/dayPartMenu";
import { Merchant } from "@/dto/merchant";
import {
  ScheduleDateType,
  ScheduledTime,
  ScheduleTimeType,
} from "@/dto/scheduledTime";
import { Service } from "@/dto/service";
import { FEATURE_FLAG_ADVANCED_ORDERING } from "@/utils/constants";
import pom from "@/utils/pom";
import {
  isOrderSchedulingEnabled,
  isWithinBusinessHours,
} from "@/utils/scheduleOrderUtils";
import { useScheduledDropdownOptions } from "@/hooks/useScheduledDropdownOptions";
import {
  getFromSessionStorage,
  saveToSessionStorage,
} from "@/utils/sessionStorageUtils";
import { SessionStorageKey } from "@/utils/storageKeys";
import { applyDataTestId } from "@/utils/testUtils";
import { useMerchantFeatureFlag } from "@/utils/useMerchantFeatureFlag";
import { DateTime } from "luxon";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { useId, useState } from "react";
import { ConfirmButton } from "../button/confirmButton";
import { RegularModal } from "../modals/modals";
import { ScheduledDateDropdown } from "../scheduledDateDropdown/scheduledDateDropdown";
import { ScheduledTimeDropdown } from "../scheduledTimeDropdown/scheduledTimeDropdown";

interface OnLoadScheduleOrderModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  merchant: Merchant;
  selectedDayPart?: DayPart | null;
}

export const OnloadScheduledOrderModal = ({
  isOpen,
  onRequestClose,
  merchant,
  selectedDayPart,
}: OnLoadScheduleOrderModalProps) => {
  const t = useTranslations();
  const searchParams = useSearchParams();
  const paramOrderType = searchParams?.get("order_type");
  const titleId = useId();
  const subtitleId = useId();
  const isDineIn =
    paramOrderType?.toLowerCase() === OrderType.DINE_IN.toLowerCase();
  const merchantService = merchant?.services.find(
    (s: Service) =>
      s.type === ServiceType.PICKUP || s.type === ServiceType.WEBSITE
  );
  const curbsideService = merchant.services.find(
    (s: Service) => s.type === ServiceType.CURBSIDE
  );
  const deliveryService = merchant?.services.find(
    (s: Service) => s.type === ServiceType.DELIVERY
  );
  const now = DateTime.now().setZone(merchant?.timezone);
  const orderType = (() => {
    const sessionTime = getFromSessionStorage<Partial<ScheduledTime>>(
      SessionStorageKey.SCHEDULED_TIME,
      {}
    );
    const defaultOrderType = sessionTime?.orderType || OrderType.PICKUP;
    const restaurantServices = [
      { type: OrderType.PICKUP, service: merchantService },
      { type: OrderType.CURBSIDE, service: curbsideService },
      { type: OrderType.DELIVERY, service: deliveryService },
    ];
    const enabledServices = restaurantServices.filter(
      ({ service }) => service?.enabled
    );

    if (enabledServices.length >= 1) {
      return enabledServices[0].type;
    }
    return defaultOrderType;
  })();
  const [scheduleDate, setScheduleDate] = useState<ScheduleDateType>({
    displayValue: t("CalculatePickTime.today"),
    dateObject: now.toJSDate(),
  });
  const [scheduleTime, setScheduleTime] = useState<ScheduleTimeType>({
    displayValue: "",
    timeObject: new Date(now.toJSDate()),
  });

  const nowInMerchantZone = now;
  // Open-check hours must stay the service (PICKUP/WEBSITE) hours only. A restaurant
  // without such a service resolves to `undefined` here, which correctly keeps the
  // "closed" verdict so the greeting modal still shows. Do NOT fall back to merchant
  // business hours for this check or the modal would disappear (behavior regression).
  const openCheckHours = merchantService?.hours;
  // Dropdown hours may fall back to merchant business hours so scheduling options can
  // still be offered when no dedicated service hours exist.
  const dropdownHours = merchantService?.hours ?? merchant?.businessHours?.hours;

  const withinBusinessHours = isWithinBusinessHours(
    nowInMerchantZone,
    openCheckHours,
    merchant?.timezone
  );
  const {
    dateOptions,
    calendarOptions,
    timeOptions,
    isDateDisabled,
    isTimeDisabled,
    onDateChange,
  } = useScheduledDropdownOptions({
    scheduleDateRange: isOrderSchedulingEnabled(merchant)
      ? merchant.scheduledOrdering.maxDays - 1
      : -1,
    businessHours: dropdownHours,
    merchant,
    selectedDayPart,
    orderType,
    scheduleDate,
    scheduleTime,
    setScheduleDate,
    setScheduleTime,
  });

  const useDatePicker = useMerchantFeatureFlag(merchant, FEATURE_FLAG_ADVANCED_ORDERING);

  if (withinBusinessHours || isDineIn) {
    return null;
  }

  return (
    <RegularModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="focus:outline-none"
      overlayClassName="bg-opacity-75"
      data-testid={applyDataTestId(pom.onloadScheduledOrderModal.modal)}
      contentLabel={t("CalculatePickTime.dialogLabel")}
      focusElementId={titleId}
      contentClassName=" bg-white sm:rounded-lg shadow-lg w-full max-w-[600px] h-full sm:h-auto sm:w-[55%] mx-auto overflow-visible px-8 "
    >
      <h2
        id={titleId}
        tabIndex={-1}
        aria-describedby={subtitleId}
        className="text-#373737 font-semibold text-2xl pt-4 pr-4 focus:outline-none"
      >
        {t("homePage.noOnlineOrdersMsg", { merchantName: merchant.name })}
      </h2>
      {dateOptions.length > 0 ? (
        <>
          <p id={subtitleId} className="pt-4">
            {t("homePage.scheduleLaterMsg")}
          </p>
          <div className="py-4 flex flex-col sm:flex-row gap-4 w-full pb-6">
            {useDatePicker ? (
              <ScheduledDatePicker
                scheduleDate={scheduleDate}
                calendarOptions={calendarOptions}
                onDateChange={onDateChange}
                timezone={merchant.timezone}
                isDisabled={isDateDisabled}
              />
            ) : (
              <div className="w-full py-6">
                <ScheduledDateDropdown
                  scheduleDate={scheduleDate}
                  dateOptions={dateOptions}
                  onDateChange={onDateChange}
                  isDisabled={isDateDisabled}
                />
              </div>
            )}
            <div className="w-full py-6">
              <ScheduledTimeDropdown
                scheduleTime={scheduleTime}
                setScheduleTime={setScheduleTime}
                timezone={merchant.timezone}
                timeOptions={timeOptions}
                isDisabled={isTimeDisabled}
              />
            </div>
          </div>
        </>
      ) : (
        <p id={subtitleId} className="pt-4 pb-4">
          {t("homePage.noScheduleMsg")}
        </p>
      )}
      {dateOptions.length > 0 && (
        <div>
          <ConfirmButton
            className="px-16 py-5 w-full text-xl font-medium leading-7 text-center text-white whitespace-nowrap rounded-lg mb-5"
            onClick={() => {
              saveToSessionStorage(SessionStorageKey.SCHEDULED_TIME, {
                orderType: orderType,
                date: scheduleDate,
                time: scheduleTime,
              });
              onRequestClose();
            }}
          >
            <p className="text-2xl font-medium">
              {t("homePage.scheduleAnOrder")}
            </p>
          </ConfirmButton>
        </div>
      )}
    </RegularModal>
  );
};
